import React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type {
  ConfigFieldOption,
  WorkflowStatus,
} from '@/services/api/generated/firmsService';

interface SingleSelectFieldProps {
  label: string;
  description?: string | null;
  value: string | undefined | null;
  options: ConfigFieldOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  status?: WorkflowStatus;
}

/**
 * Generic single-select field renderer.
 * Compact styling to match workflow row typography and spacing.
 */
export const SingleSelectField: React.FC<SingleSelectFieldProps> = ({
  label,
  description,
  value,
  options,
  onChange,
  disabled = false,
  isLoading = false,
  status,
}) => {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-medium text-zinc-950 dark:text-white">
          {label}
        </div>
        {status === 'coming_soon' && (
          <Badge color="zinc" className="text-[10px] py-0 px-1">
            Not Live
          </Badge>
        )}
      </div>
      {description && (
        <div className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400">
          {description}
        </div>
      )}
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || isLoading}
        className="w-full px-2 py-1 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <option value="" disabled>
          Select...
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};
